import { useState } from 'react';
import WizardSelectComponent from 'components/v2/epd-wizard-inputs/WizardSelect';
import WizardTextAreaComponent from 'components/v2/epd-wizard-inputs/WizardTextArea';
import WizardTextInputComponent from 'components/v2/epd-wizard-inputs/WizardTextInput';
import { LabelGroup } from 'components/v2/form/styled';
import { HelpBox } from 'components/v2/help-boxes';
import { useTranslation } from 'react-i18next';
import { EPDModel, MaterialPropertyModel, Subtype } from 'services/EpdClient';
import styled from 'styled-components';
import { FieldContainer, FieldPanelInput, HorizontalDivider } from 'styles/v2/Styles.styled';
import { EPDBasedOnUnitEnum, Option } from 'types/types';
import MaterialPropertiesCopySection from './sections/MaterialPropertiesCopySection';
import DataSourcesCopySection from './sections/DataSourcesCopySection';
import EditDialog from './EditDialog';
import { useDictionaryValues, useReferencePackageVersions15804 } from 'services/api/queries';
import { getOptionsFromDictionary } from 'util/utils';
import { EPD_DICTIONARY_CODES } from 'services/api/constants';
import BiogenicCarbonContentCopySection from './sections/BiogenicCarbonContentCopySection';
import { reactSelectPropsForHtmlLabel } from 'util/reactSelectPropsForHtmlLabel';
import TooltipHelpIcon from 'components/v2/icons/TooltipHelpIcon';

type TProps = {
  isOpened: boolean;
  onHide: any;
  epd: EPDModel;
  onChangeEpd: any;
  flowPropertiesOptions: Option[];
  materialProperties: MaterialPropertyModel[];
  biogenicCarbonContentUnits?: Option[];
  isConstruction?: boolean;
};

const EditMachineReadableDialog = ({
  isOpened,
  onHide,
  epd,
  onChangeEpd,
  flowPropertiesOptions,
  materialProperties,
  biogenicCarbonContentUnits,
  isConstruction,
}: TProps) => {
  const { t } = useTranslation();
  const referencePackageVersions15804 = useReferencePackageVersions15804().data;

  const selectedFlowPropertyId = epd?.referenceFlow?.flowPropertyId ?? null;
  const selectedConversionFactor = epd?.referenceFlow?.conversionFactor ?? null;

  const [selectedFunctionalUnitDescription, setSelectedFunctionalUnitDescription] = useState<string | undefined>(
    epd?.functionalUnitDescription
  );

  const dictionariesQuery = useDictionaryValues();

  const epdBasedOnUnitOptions = getOptionsFromDictionary(dictionariesQuery.data, EPD_DICTIONARY_CODES.EPD_BASED_ON_UNIT);

  const selectedEPDBasedOnUnit = epdBasedOnUnitOptions.find(
    (x) => epd?.epdBasedOnUnit && x.value === Object.keys(EPDBasedOnUnitEnum).indexOf(epd?.epdBasedOnUnit).toString()
  );

  const getSubTypeOptionLabel = (key: string) => {
    switch (key) {
      case Subtype.Specific:
        return 'Specific (single product)';
      case Subtype.Average:
        return 'Average (multiple products)';
      case Subtype.Representative:
        return 'Representative (Sector EPD)';
      case Subtype.Template:
        return 'Template (product not yet on the market)';
      default:
        return '';
    }
  };

  const getSubTypeOptionDisabled = (key: string) => {
    switch (key) {
      case Subtype.Average:
        return epd?.isSectorEPD;
      case Subtype.Specific:
        return epd?.isSectorEPD;
      default:
        return false;
    }
  };

  const productTypeOptions = getOptionsFromDictionary(dictionariesQuery.data, EPD_DICTIONARY_CODES.PRODUCT_TYPE);

  const subtypeOptions: Option[] = (Object.keys(Subtype) as Array<keyof typeof Subtype>).map((key) => ({
    label: getSubTypeOptionLabel(key),
    value: Subtype[key].toString(),
    isDisabled: getSubTypeOptionDisabled(key),
  }));

  const createFlowModel = (name: string | null, flowPropertyId: string | null, conversionFactor: number | null) => {
    return {
      name,
      flowPropertyItem:
        flowPropertyId || conversionFactor
          ? {
              flowPropertyId: flowPropertyId,
              conversionFactor: conversionFactor,
            }
          : null,
    };
  };

  function handleProductFlowValueChange(event: any) {
    const { value } = event.target;
    const formattedProductFlowValue = value.replace(/^0+(?=\d)/, ''); // Removes leading zeros, except one before a period
    event.target.value = formattedProductFlowValue;
  }

  const handleProductFlowValueKeyDown = (event: any) => {
    const {
      code,
      target: { value },
    } = event;
    const fractionalNumWithOneDigitPattern = /^0\.\d$/;
    if (code === 'Backspace' && fractionalNumWithOneDigitPattern.test(value)) {
      event.target.value = '';
      return;
    }
    if (code !== 'Minus') {
      return;
    }
    event.preventDefault();
  };

  const handleProductFlowValuePaste = (event: any) => {
    event.preventDefault();
    const clipboardData = event.clipboardData || window.Clipboard;
    const pastedValue = clipboardData.getData('text');

    if (pastedValue === '0') {
      // Inserts the text at the current caret position
      document.execCommand('insertText', false, '0.01');
      return;
    }

    let formattedProductFlowValue = pastedValue
      .replace(/^0+(?=\d)/, '') // Removes leading zeros, except one before a period
      .replace(/-/g, ''); // Removes minuses

    document.execCommand('insertText', false, formattedProductFlowValue);
  };

  const referencePackageVersions15804Options: Option[] =
    referencePackageVersions15804?.versions.map((r) => ({
      label: r.name,
      value: r.id,
    })) ?? [];

  const changeEPDBasedOnUnit = (epdBasedOnUnit?: any, functionalUnitDescription?: string) => {
    let newValue: any = {
      epdBasedOnUnit: epdBasedOnUnit,
      functionalUnitDescription: functionalUnitDescription,
    };

    return newValue;
  };

  return (
    <EditDialog header="Information for machine readable EPDs" isOpened={isOpened} onHide={onHide} maximizable>
      <HelpBox>
        Please fill-in the required information below. The filled data will be auto saved and available in the table.
      </HelpBox>
      <FieldPanelInput>
        <FieldContainer>
          <WizardTextInputComponent
            label="Global trade item number"
            name="gtin"
            tooltip={t('epdWizard.wizardStep2.tooltips.gtin')}
            value={epd?.gtin}
            onChanged={onChangeEpd}
            type="number"
          />
        </FieldContainer>

        <HorizontalDivider />

        <FieldContainer>
          <WizardSelectComponent
            isClearable={false}
            required={isConstruction}
            label="EPD based on declared or functional unit"
            tooltip={t('epdWizard.wizardStep2.tooltips.basedOnUnit')}
            tooltipPosition="right"
            options={epdBasedOnUnitOptions}
            name="epdBasedOnUnit"
            value={selectedEPDBasedOnUnit}
            onChanged={(name: any, value: any) =>
              onChangeEpd('epdBasedOnUnit', changeEPDBasedOnUnit(value, selectedFunctionalUnitDescription))
            }
          />
        </FieldContainer>
        {epd?.epdBasedOnUnit === EPDBasedOnUnitEnum.FunctionalUnit && (
          <FieldContainer>
            <WizardTextAreaComponent
              label="FUNCTIONAL UNIT DESCRIPTION"
              required={isConstruction}
              value={epd?.functionalUnitDescription}
              state={epd?.functionalUnitDescriptionState?.actionState}
              onChanged={(fieldName: string, value: any) => {
                setSelectedFunctionalUnitDescription(value ?? null);
                onChangeEpd(fieldName, changeEPDBasedOnUnit(EPDBasedOnUnitEnum.FunctionalUnit, value ?? null));
              }}
              name="functionalUnitDescription"
              tooltip={t('epdWizard.wizardStep2.tooltips.funcUnitDesc')}
              tooltipPosition="right"
            />
          </FieldContainer>
        )}

        <HorizontalDivider />

        <FieldContainer>
          <LabelGroup>
            <span style={{ fontWeight: 'bold' }}>
              {t(
                `epdCompiler.step3.lcaSpecificationTab.properties.referenceFlow.title.${
                  selectedEPDBasedOnUnit?.value ??
                  Object.keys(EPDBasedOnUnitEnum).indexOf(EPDBasedOnUnitEnum.DeclaredUnit).toString()
                }`
              )}
            </span>
            <TooltipHelpIcon
              content={t(
                `epdCompiler.step3.lcaSpecificationTab.properties.referenceFlow.tooltip.${
                  selectedEPDBasedOnUnit?.value ??
                  Object.keys(EPDBasedOnUnitEnum).indexOf(EPDBasedOnUnitEnum.DeclaredUnit).toString()
                }`
              )}
              position={'right'}
            />
          </LabelGroup>
          <HelpBox style={{ marginTop: 0 }}>{'Example: Cement (CEM II 32.5). Unit: Mass (kg). Value: 1'}</HelpBox>
          <FieldContainer>
            <WizardTextInputComponent
              label="Product flow name"
              required={isConstruction}
              name="productFlow"
              tooltip="Please provide product flow name, select unit, and provide a value."
              value={epd?.referenceFlow?.name ?? ''}
              onChanged={(fieldName: string, value: any) => {
                onChangeEpd(
                  fieldName,
                  createFlowModel(value ? value : '', selectedFlowPropertyId, selectedConversionFactor)
                );
              }}
              tooltipPosition="right"
            />
          </FieldContainer>
        </FieldContainer>
      </FieldPanelInput>
      <FieldContainer>
        <FieldPanel>
          <WizardSelectComponent
            label="Unit"
            tooltip="Indicates the declared unit or functional unit."
            tooltipPosition="right"
            name="productFlow"
            options={flowPropertiesOptions}
            value={flowPropertiesOptions.find((o) => o.value === selectedFlowPropertyId)}
            required={isConstruction}
            onChanged={(_: string, flowPropertyId: any) => {
              onChangeEpd(
                'productFlow',
                createFlowModel(epd?.referenceFlow?.name ?? null, flowPropertyId ?? null, selectedConversionFactor)
              );
            }}
            {...reactSelectPropsForHtmlLabel}
          />
          <WizardTextInputComponent
            label="Value"
            type="number"
            min="0.1"
            step="0.1"
            required={isConstruction}
            name="productFlow"
            tooltip="Numeric field."
            tooltipPosition="right"
            onKeyDown={handleProductFlowValueKeyDown}
            onPaste={handleProductFlowValuePaste}
            onChange={handleProductFlowValueChange}
            onChanged={(_: string, value: number) => {
              onChangeEpd('productFlow', createFlowModel(epd?.referenceFlow?.name ?? null, selectedFlowPropertyId, value));
            }}
            value={selectedConversionFactor ?? ''}
          />
        </FieldPanel>
      </FieldContainer>

      <HorizontalDivider />

      <FieldContainer>
        <WizardSelectComponent
          label={t('epdWizard.wizardStep2.properties.productToDeclare.title')}
          tooltip={t('epdWizard.wizardStep2.properties.productToDeclare.tooltip')}
          tooltipPosition="right"
          options={productTypeOptions}
          name="productType"
          value={epd?.productType ? productTypeOptions.find((o) => o.label === epd.productType) : null}
          onChanged={onChangeEpd}
          isClearable={false}
          required={isConstruction}
        />
      </FieldContainer>

      <HorizontalDivider />

      <BiogenicCarbonContentCopySection
        epd={epd}
        biogenicCarbonContentUnits={biogenicCarbonContentUnits}
        onChangeEpd={onChangeEpd}
      />

      <HorizontalDivider />

      <MaterialPropertiesCopySection
        epdMaterialProperties={epd?.materialProperties}
        materialProperties={materialProperties}
        onSave={(newValues) => onChangeEpd('MaterialProperties', newValues)}
      />

      <HorizontalDivider />

      <FieldContainer>
        <WizardTextInputComponent
          label="GEOGRAPHICAL SCOPE DESCRIPTION"
          value={epd?.geographicalDescription}
          onChanged={onChangeEpd}
          name="geographicalDescription"
          tooltip={t('epdWizard.wizardStep2.tooltips.geo')}
          tooltipPosition="right"
        />
      </FieldContainer>

      <HorizontalDivider />

      <FieldContainer>
        <WizardTextAreaComponent
          label="TECHNOLOGY DESCRIPTION INCLUDING BACKGROUND SYSTEM"
          required={isConstruction}
          value={epd?.technologyDescription}
          onChanged={onChangeEpd}
          name="technologyDescription"
          tooltip={t('epdWizard.wizardStep2.tooltips.technology')}
          tooltipPosition="right"
        />
      </FieldContainer>
      <HorizontalDivider />
      <FieldContainer>
        <WizardTextAreaComponent
          label="TECHNICAL PURPOSE OF PRODUCT OR PROCESS"
          required={isConstruction}
          value={epd?.technicalPurpose}
          onChanged={onChangeEpd}
          name="technicalPurpose"
          tooltip={t('epdWizard.wizardStep2.tooltips.technicalPurpose')}
          tooltipPosition="right"
        />
      </FieldContainer>
      <HorizontalDivider />

      <FieldContainer>
        <WizardSelectComponent
          isClearable={false}
          required={isConstruction}
          label="Subtype for ILCD+EPD"
          tooltip={t('epdWizard.wizardStep2.tooltips.subType')}
          tooltipPosition="right"
          options={subtypeOptions}
          name="subtype"
          value={{ value: epd?.subtype, label: epd?.subtype }}
          onChanged={onChangeEpd}
        />
      </FieldContainer>

      <HorizontalDivider />

      <DataSourcesCopySection
        initialDataSources={epd.epdDataSources ?? []}
        onSave={(newValue) => onChangeEpd('DataSourcesProperties', newValue)}
        required={isConstruction ?? false}
      />

      {isConstruction && (
        <>
          <HorizontalDivider />

          <FieldContainer>
            <WizardSelectComponent
              label="Version of the EN 15804 reference package"
              options={referencePackageVersions15804Options}
              name="referencePackageVersion15804"
              value={
                epd?.referencePackageVersion15804
                  ? referencePackageVersions15804Options.find((o) => o.value === epd.referencePackageVersion15804)
                  : null
              }
              onChanged={onChangeEpd}
              isClearable={false}
              required
            />
          </FieldContainer>

          <FieldContainer>
            <WizardTextInputComponent
              label="Characterisation methods"
              value={epd?.characterisationMethods}
              onChanged={onChangeEpd}
              name="characterisationMethods"
              tooltip={t('epdWizard.wizardStep2.tooltips.characterisationMethods')}
              tooltipPosition={'right'}
            />
          </FieldContainer>
        </>
      )}

      <HorizontalDivider />

      <FieldContainer>
        <WizardTextInputComponent
          label="Additional information about the underlying LCA-based information"
          value={epd?.lcaBasedDescription}
          onChanged={onChangeEpd}
          name="lcaBasedDescription"
          tooltip='This may include information about assumptions, deviations, justification, cut-off rules, data quality, and allocation. This includes also additional information that PCR and c-PCR(s) may require in given circumstances. This field corresponds to the "General Comment on Data Set" section in the ILCD+EPD format.'
          tooltipPosition={'left'}
        />
      </FieldContainer>

      <HorizontalDivider />

      <FieldContainer>
        <WizardTextAreaComponent
          label="USE ADVICE FOR THE EPD"
          required={isConstruction}
          value={epd?.useAdvice}
          onChanged={onChangeEpd}
          name="useAdvice"
          tooltip={t('epdWizard.wizardStep2.tooltips.useForDatasets')}
          tooltipPosition={'right'}
        />
      </FieldContainer>
    </EditDialog>
  );
};

const FieldPanel = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  grid-column-gap: 30px;
  grid-row-gap: 30px;
  justify-content: start;
  ${(props) => props.theme.media.tablet} {
    grid-template-columns: 1fr 1fr;
  }
  ${(props) => props.theme.media.desktop} {
    grid-template-columns: 1fr 1fr;
  }
`;

export default EditMachineReadableDialog;
